<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import type { AppContext } from '../app';
  import { createTelemetry, formatMet, usesMet } from '../core/telemetry';
  import { lang, t } from '../i18n';
  import { currentDate, focus, followTarget } from '../state/stores';
  import { spacecraftCandidates } from './activeSpacecraft';
  import { formatAu, formatInt, formatKm, formatLightTime, formatSpeed } from './format';

  let { ctx }: { ctx: AppContext } = $props();

  const candidates = $derived(spacecraftCandidates(ctx.targets, $followTarget, $focus));
  let scId = $state<string | null>(null);

  // Readings refresh at 10 Hz (S2-04); the scene writes into a reused object.
  const reading = createTelemetry();
  let near = $state<string | null>(null);
  let speed = $state(0);
  let sun = $state(0);
  let light = $state(0);
  let day = $state(0);
  let nearKm = $state(Number.NaN);
  let nearSpeed = $state(Number.NaN);
  /** Silent spacecraft (D11): since when (UTC ms), else null. */
  let silentSince = $state<number | null>(null);

  let raf = 0;
  let last = 0;
  function tick(now: number): void {
    raf = requestAnimationFrame(tick);
    if (now - last < 100) return;
    last = now;
    near = null;
    scId = null;
    for (const id of candidates) {
      near = ctx.scene.telemetry(id, reading);
      if (near) {
        scId = id;
        break;
      }
    }
    if (!near) return;
    speed = reading.speedKms;
    sun = reading.sunDistanceAu;
    light = reading.lightTimeS;
    day = reading.missionDay;
    nearKm = reading.nearDistanceKm;
    nearSpeed = reading.nearSpeedKms;
    silentSince = scId ? (ctx.scene.lifeOf(scId)?.silentSince ?? null) : null;
  }
  onMount(() => (raf = requestAnimationFrame(tick)));
  onDestroy(() => cancelAnimationFrame(raf));

  const inBubble = $derived(near !== null && near !== 'sun' && Number.isFinite(nearKm));
  // near a moon the altitude above its surface (D13: Columbia ~110 km above the Moon, not 1,850 km from its centre)
  const aboveMoon = $derived(inBubble && ctx.targets.get(near!)?.bodyKind === 'moon');
  const shownKm = $derived(aboveMoon ? Math.max(0, nearKm - ctx.scene.bodyRadiusKm(near!)) : nearKm);
  // short missions (< 30 days, Apollo) are told in mission elapsed time from the mission's launch (D13)
  const mission = $derived(scId ? ctx.missions.find((m) => m.id === ctx.targets.get(scId!)?.mission) : undefined);
  const met = $derived(
    mission && usesMet(mission.launch, mission.end) ? formatMet($currentDate - mission.launch) : null,
  );
  const nearName = $derived(near ? (ctx.targets.get(near)?.name[$lang] ?? near) : '');
  const km = $derived(formatKm(shownKm, $lang));
  const name = $derived(scId ? (ctx.targets.get(scId)?.name[$lang] ?? scId) : '');
  /** "23 Jan 2003" / "23. Jan. 2003" (UTC) — the day the contact ended (the end date counts until its end). */
  function formatDay(ms: number, l: string): string {
    return new Intl.DateTimeFormat(l === 'de' ? 'de-DE' : 'en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(ms - 1);
  }
</script>

{#if near}
  <!-- on phones only while following (S4-15): the readings belong to the ride-along, not to the overview -->
  <dl
    class="panel telemetry"
    class:idle={!$followTarget}
    aria-label={$t('telemetry.label', { name })}
    data-testid="telemetry"
  >
    <div class="cell">
      {#if inBubble && Number.isFinite(nearSpeed)}
        <dt class="caps-label">{$t('telemetry.speedRel')}</dt>
        <dd>{formatSpeed(nearSpeed, $lang)}<span class="unit">km/s</span></dd>
      {:else}
        <dt class="caps-label">{$t('telemetry.speed')}</dt>
        <dd>{formatSpeed(speed, $lang)}<span class="unit">km/s</span></dd>
      {/if}
    </div>
    <div class="cell">
      {#if aboveMoon}
        <dt class="caps-label">{$t('telemetry.altitude', { body: nearName })}</dt>
        <dd>{km.value}<span class="unit">km</span></dd>
      {:else if inBubble}
        <dt class="caps-label">{nearName}</dt>
        <dd>{km.value}<span class="unit">{km.millions ? $t('unit.millionKm') : 'km'}</span></dd>
      {:else}
        <dt class="caps-label">{$t('telemetry.sun')}</dt>
        <dd>{formatAu(sun, $lang)}<span class="unit">{$t('unit.au')}</span></dd>
      {/if}
    </div>
    <div class="cell">
      {#if silentSince !== null}
        <!-- no signal any more: when it stopped instead of the light time -->
        <dt class="caps-label">{$t('telemetry.silentSince')}</dt>
        <dd class="date" data-testid="silent-since">{formatDay(silentSince, $lang)}</dd>
      {:else}
        <dt class="caps-label">{$t('telemetry.signal')}</dt>
        <dd>{formatLightTime(light, $lang)}</dd>
      {/if}
    </div>
    <div class="cell">
      {#if met}
        <dt class="caps-label"><abbr title={$t('telemetry.metLong')}>{$t('telemetry.met')}</abbr></dt>
        <dd data-testid="met">{met}</dd>
      {:else}
        <dt class="caps-label">{$t('telemetry.day')}</dt>
        <dd>{formatInt(day, $lang)}</dd>
      {/if}
    </div>
  </dl>
{/if}

<style>
  .telemetry {
    width: 100%;
    max-width: 760px;
    box-sizing: border-box;
    margin: 0;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .cell {
    min-width: 0;
    padding: 6px 10px 7px;
  }
  .cell + .cell {
    border-left: var(--rule-w) solid var(--rule);
  }
  dt {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  abbr {
    text-decoration: none;
    cursor: help;
  }
  dd.date {
    font-size: 0.8rem;
  }
  dd {
    margin: 0;
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    font-size: 0.95rem;
    font-weight: 600;
    white-space: nowrap;
  }
  .unit {
    margin-left: 0.3em;
    font-size: 0.75em;
    font-weight: 500;
    color: var(--fg-muted);
  }
  @media (max-width: 899px) {
    .telemetry.idle {
      display: none;
    }
    .cell {
      padding: 1px 8px 2px;
    }
    dt {
      font-size: 0.6rem;
    }
    dd {
      line-height: 1.25;
    }
  }
  @media (max-width: 480px) {
    .cell {
      padding: 1px 7px 2px;
    }
    dd {
      font-size: 0.85rem;
      letter-spacing: -0.02em;
    }
  }
</style>
